import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { INotification } from '../models/notification.interface';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationsSubject = new BehaviorSubject<INotification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();
  
  public unreadCount$ = this.notifications$.pipe(
    map(notifications => notifications.filter(n => !n.read).length)
  );

  constructor() {
    this.loadNotifications();
  }

  private loadNotifications() {
    const stored = localStorage.getItem('user_notifications');
    if (stored) {
      try {
        const notifications = JSON.parse(stored);
        // Convert date strings back to Date objects
        notifications.forEach((n: any) => n.date = new Date(n.date));
        this.notificationsSubject.next(notifications);
      } catch (e) {
        console.error('Failed to parse notifications', e);
      }
    }
  }

  private saveNotifications(notifications: INotification[]) {
    localStorage.setItem('user_notifications', JSON.stringify(notifications));
    this.notificationsSubject.next(notifications);
  }

  addNotification(notification: Omit<INotification, 'id' | 'date' | 'read'>) {
    const newNotification: INotification = {
      ...notification,
      id: this.generateId(),
      date: new Date(),
      read: false
    };

    const current = this.notificationsSubject.value;
    const updated = [newNotification, ...current];
    this.saveNotifications(updated);
  }

  markAsRead(id: string) {
    const current = this.notificationsSubject.value;
    const updated = current.map(n => n.id === id ? { ...n, read: true } : n);
    this.saveNotifications(updated);
  }

  markAllAsRead() {
    const current = this.notificationsSubject.value;
    const updated = current.map(n => ({ ...n, read: true }));
    this.saveNotifications(updated);
  }

  clearAll() {
    this.saveNotifications([]);
  }

  getUnreadCount(): number {
    return this.notificationsSubject.value.filter(n => !n.read).length;
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }
}
